<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import DatePicker from "../../components/DatePicker.vue";
import { useModalStore } from "../../stores/modalStore.ts";
import { formatCalendarDateForDisplay } from "../../utils/dateFormatting.ts";
import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";
import {
  buildProjectedPoAfterMove,
  formatMoveCubeFt3,
  getAvailableCapacityFt3,
  getRawPoCube,
  parseShipDateToDate,
  parseWholeQuantityInput,
  planProportionalLeftoverMove,
  projectPoTotalsAfterMove,
  type EditableShippingPo,
  type LeftoverInventoryMoveApplyRequest,
  type LeftoverInventoryMoveApplyResult,
  type LeftoverInventoryMoveDraft,
  type LeftoverInventoryMoveSnapshot,
} from "./shippingPoEditing.ts";

const props = defineProps<{
  newPoId?: string;
  idPrefix?: string;
  rows: BulkOrderCreationRow[];
  targets: EditableShippingPo[];
  initialDraft: LeftoverInventoryMoveDraft;
  getCurrentState: () => LeftoverInventoryMoveSnapshot;
  onDraftChange: (draft: LeftoverInventoryMoveDraft) => void;
  onApply: (
    request: LeftoverInventoryMoveApplyRequest
  ) => LeftoverInventoryMoveApplyResult;
  onDismiss: () => void;
}>();

function scopedId(id: string): string {
  return props.idPrefix ? `${props.idPrefix}-${id}` : id;
}

const modalStore = useModalStore();
const draft = ref(cloneDraft(props.initialDraft));
const currentRows = ref([...props.rows]);
const currentTargets = ref([...props.targets]);
const overrideConfirmed = ref(false);
const notice = ref("");
const result = ref<LeftoverInventoryMoveApplyResult>();
const firstFocusRef = ref<HTMLButtonElement | null>(null);
let triggerBeforeOpen: HTMLElement | null = null;

function cloneDate(date: Date | undefined): Date | undefined {
  if (!date) return;
  return new Date(date);
}

function cloneDraft(
  value: LeftoverInventoryMoveDraft
): LeftoverInventoryMoveDraft {
  return {
    ...value,
    selectedSkus: [...value.selectedSkus],
    sourceLeftoversBySku: { ...value.sourceLeftoversBySku },
    quantitiesBySku: { ...value.quantitiesBySku },
    targetDatesByPo: Object.fromEntries(
      Object.entries(value.targetDatesByPo).map(([poId, dates]) => [
        poId,
        {
          ...(dates.shipDateValue
            ? { shipDateValue: cloneDate(dates.shipDateValue) }
            : {}),
          ...(dates.arrivalDate
            ? { arrivalDate: cloneDate(dates.arrivalDate) }
            : {}),
        },
      ])
    ),
  };
}

function persistDraft(): void {
  props.onDraftChange(cloneDraft(draft.value));
}

const selectedRows = computed(() =>
  draft.value.selectedSkus.flatMap((sku) => {
    const row = currentRows.value.find((candidate) => candidate.sku === sku);
    return row ? [row] : [];
  })
);

const currentTarget = computed(() =>
  currentTargets.value.find((po) => po.id === draft.value.targetPoId)
);

function ensureTargetDates(po: EditableShippingPo): void {
  if (draft.value.targetDatesByPo[po.id]) return;
  draft.value.targetDatesByPo[po.id] = {
    ...(po.shipDateValue ? { shipDateValue: cloneDate(po.shipDateValue) } : {}),
    ...(po.arrivalDate ? { arrivalDate: cloneDate(po.arrivalDate) } : {}),
  };
}

const selectedTargetDates = computed(() => {
  const po = currentTarget.value;
  if (!po) return;
  return draft.value.targetDatesByPo[po.id];
});

function formatDate(date: Date | undefined): string {
  return date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Not set";
}

function getPoDisplayNumber(po: EditableShippingPo): number {
  return po.sequence;
}

function getDraftQuantity(sku: string): number | undefined {
  const raw = draft.value.quantitiesBySku[sku] ?? "";
  const parsed = parseWholeQuantityInput(raw);
  if (parsed === undefined || parsed < 0) return;
  return parsed;
}

const invalidRows = computed(() => {
  const reasons = new Map<string, string>();
  for (const sku of draft.value.selectedSkus) {
    const row = currentRows.value.find((candidate) => candidate.sku === sku);
    if (!row) {
      reasons.set(sku, "No longer has leftover inventory.");
      continue;
    }
    const quantity = getDraftQuantity(sku);
    if (quantity === undefined) {
      reasons.set(sku, "Enter a whole quantity of 0 or more.");
    } else if (quantity > row.requestedUnits) {
      reasons.set(sku, `Only ${String(row.requestedUnits)} units remain.`);
    }
  }
  return reasons;
});

const moveQuantities = computed(() => {
  const quantities: Record<string, number> = {};
  for (const row of selectedRows.value) {
    const quantity = getDraftQuantity(row.sku);
    if (
      quantity !== undefined &&
      quantity <= row.requestedUnits &&
      !invalidRows.value.has(row.sku)
    ) {
      quantities[row.sku] = quantity;
    }
  }
  return quantities;
});

function sourceRowsForQuantities(
  quantities: Record<string, number>
): BulkOrderCreationRow[] {
  const skus = new Set(
    Object.entries(quantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([sku]) => sku)
  );
  return currentRows.value.filter((row) => skus.has(row.sku));
}

const selectedProjectedTotals = computed(() => {
  const po = currentTarget.value;
  if (!po) return;
  return projectPoTotalsAfterMove(
    po,
    sourceRowsForQuantities(moveQuantities.value),
    moveQuantities.value
  );
});
const selectedProjectedRemaining = computed(() => {
  const po = currentTarget.value;
  if (!po) return;
  return getAvailableCapacityFt3(
    buildProjectedPoAfterMove(
      po,
      sourceRowsForQuantities(moveQuantities.value),
      moveQuantities.value
    )
  );
});
const selectedProjectedCube = computed(() => {
  const po = currentTarget.value;
  if (!po) return;
  return getRawPoCube(
    buildProjectedPoAfterMove(
      po,
      sourceRowsForQuantities(moveQuantities.value),
      moveQuantities.value
    )
  );
});
const selectedCapacityMeasurable = computed(() => {
  const po = currentTarget.value;
  if (!po) return false;
  return planProportionalLeftoverMove(
    po,
    sourceRowsForQuantities(moveQuantities.value),
    moveQuantities.value
  ).capacityMeasurable;
});
const selectedMoveOverCapacity = computed(
  () =>
    selectedProjectedRemaining.value !== undefined &&
    selectedProjectedRemaining.value < 0
);
const capacityError = computed(() => {
  if (draft.value.mode !== "fit") return "";
  if (!selectedCapacityMeasurable.value) {
    return "Capacity or item cube cannot be measured. Choose the explicit override to continue.";
  }
  if (selectedMoveOverCapacity.value) {
    return "The entered quantities exceed shared PO capacity. Recalculate the proportional fit or adjust quantities.";
  }
  return "";
});

const availabilityWarnings = computed(() => {
  const targetShipDate = selectedTargetDates.value?.shipDateValue;
  const warnings: string[] = [];
  for (const row of selectedRows.value) {
    if ((getDraftQuantity(row.sku) ?? 0) <= 0) continue;
    const sourceShipDate = parseShipDateToDate(row.shipDate);
    if (!sourceShipDate || !targetShipDate) {
      warnings.push(
        `SKU ${row.sku}: availability could not be verified because a ship date is missing.`
      );
    } else if (sourceShipDate.getTime() > targetShipDate.getTime()) {
      warnings.push(
        `SKU ${row.sku}: available ${formatDate(sourceShipDate)}, after target ship date ${formatDate(targetShipDate)}.`
      );
    }
  }
  return warnings;
});

function getIssueForSku(sku: string): string | undefined {
  return invalidRows.value.get(sku);
}

function getProjectedTarget(po: EditableShippingPo) {
  const targetDates = draft.value.targetDatesByPo[po.id];
  const totals = projectPoTotalsAfterMove(
    po,
    sourceRowsForQuantities(moveQuantities.value),
    moveQuantities.value
  );
  const projectedPo = buildProjectedPoAfterMove(
    po,
    sourceRowsForQuantities(moveQuantities.value),
    moveQuantities.value
  );
  return {
    currentCapacity: getAvailableCapacityFt3(po),
    projectedCube: getRawPoCube(projectedPo),
    projectedLoad: totals.loadPercent,
    projectedCapacityMeasurable:
      getAvailableCapacityFt3(projectedPo) !== undefined,
    shipDate:
      po.id === draft.value.targetPoId
        ? targetDates?.shipDateValue
        : po.shipDateValue,
    arrivalDate:
      po.id === draft.value.targetPoId
        ? targetDates?.arrivalDate
        : po.arrivalDate,
  };
}

function updateQuantity(sku: string, value: string): void {
  draft.value.quantitiesBySku[sku] = value;
  notice.value = "";
  persistDraft();
}

function stepQuantity(row: BulkOrderCreationRow, step: -1 | 1): void {
  const quantity = getDraftQuantity(row.sku);
  if (quantity === undefined || getIssueForSku(row.sku)) return;
  const next = quantity + step;
  if (next >= 0 && next <= row.requestedUnits) {
    updateQuantity(row.sku, String(next));
  }
}

function updateTargetDate(
  field: "shipDateValue" | "arrivalDate",
  date: Date | undefined
): void {
  const po = currentTarget.value;
  if (!po) return;
  ensureTargetDates(po);
  const dates = draft.value.targetDatesByPo[po.id];
  if (!dates) return;
  dates[field] = date ? new Date(date) : undefined;
  notice.value = "";
  persistDraft();
}

function requestedQuantitiesFromDraft(): Record<string, number> {
  const requested: Record<string, number> = {};
  for (const row of selectedRows.value) {
    const quantity = getDraftQuantity(row.sku);
    if (
      quantity !== undefined &&
      quantity <= row.requestedUnits &&
      !invalidRows.value.has(row.sku)
    ) {
      requested[row.sku] = quantity;
    }
  }
  return requested;
}

function recalculateProportionalFit(): boolean {
  const po = currentTarget.value;
  if (!po) return false;
  const requested = requestedQuantitiesFromDraft();
  const plan = planProportionalLeftoverMove(
    po,
    sourceRowsForQuantities(requested),
    requested
  );
  if (!plan.capacityMeasurable) return false;
  draft.value.fitInitialized = true;
  draft.value.quantitiesBySku = {
    ...draft.value.quantitiesBySku,
    ...Object.fromEntries(
      Object.entries(plan.quantitiesBySku).map(([sku, quantity]) => [
        sku,
        String(quantity),
      ])
    ),
  };
  persistDraft();
  return true;
}

function setMode(mode: "fit" | "override"): void {
  draft.value.mode = mode;
  overrideConfirmed.value = false;
  if (mode === "override") {
    for (const row of selectedRows.value) {
      draft.value.quantitiesBySku[row.sku] = String(row.requestedUnits);
    }
    notice.value = "";
  } else {
    notice.value = recalculateProportionalFit()
      ? ""
      : "Capacity cannot be measured for this target. Use the explicit override.";
  }
  persistDraft();
}

function setTargetPo(id: string): void {
  const target = currentTargets.value.find((po) => po.id === id);
  if (!target) return;
  draft.value.targetPoId = id;
  ensureTargetDates(target);
  if (draft.value.mode === "fit") recalculateProportionalFit();
  notice.value = "";
  persistDraft();
}

function refreshCurrentState(): boolean {
  const latest = props.getCurrentState();
  const previousRows = currentRows.value;
  currentRows.value = [...latest.rows];
  currentTargets.value = [...latest.targets];
  let changed = false;

  for (const sku of draft.value.selectedSkus) {
    const row = currentRows.value.find((candidate) => candidate.sku === sku);
    if (!row) {
      draft.value.selectedSkus = draft.value.selectedSkus.filter(
        (selectedSku) => selectedSku !== sku
      );
      draft.value.quantitiesBySku = Object.fromEntries(
        Object.entries(draft.value.quantitiesBySku).filter(
          ([selectedSku]) => selectedSku !== sku
        )
      );
      draft.value.sourceLeftoversBySku = Object.fromEntries(
        Object.entries(draft.value.sourceLeftoversBySku).filter(
          ([selectedSku]) => selectedSku !== sku
        )
      );
      changed = true;
      notice.value = `SKU ${sku} no longer has leftover inventory and was removed from this move.`;
      continue;
    }

    const previousRow = previousRows.find((candidate) => candidate.sku === sku);
    const previousLeftover =
      draft.value.sourceLeftoversBySku[sku] ?? previousRow?.requestedUnits;
    const quantity = getDraftQuantity(sku);
    if (
      previousLeftover !== undefined &&
      row.requestedUnits !== previousLeftover
    ) {
      changed = true;
      notice.value = `SKU ${sku} leftover quantity changed; review the refreshed quantities.`;
    }
    if (
      previousLeftover !== undefined &&
      row.requestedUnits < previousLeftover &&
      quantity !== undefined &&
      quantity > row.requestedUnits
    ) {
      draft.value.quantitiesBySku[sku] = String(row.requestedUnits);
      changed = true;
      notice.value = `SKU ${sku} leftover quantity changed; its move quantity was reduced to ${String(row.requestedUnits)}.`;
    }
    draft.value.sourceLeftoversBySku[sku] = row.requestedUnits;
  }

  if (!currentTargets.value.some((po) => po.id === draft.value.targetPoId)) {
    const replacement = currentTargets.value[0];
    if (replacement) {
      draft.value.targetPoId = replacement.id;
      ensureTargetDates(replacement);
      changed = true;
      notice.value =
        "The previous target PO is unavailable; select a current target and review the move again.";
    }
  }
  return changed;
}

function handleReview(): void {
  const changed = refreshCurrentState();
  if (
    draft.value.mode === "fit" &&
    (!selectedCapacityMeasurable.value || selectedMoveOverCapacity.value)
  ) {
    notice.value = capacityError.value;
    return;
  }
  if (draft.value.selectedSkus.length === 0 || !currentTarget.value) {
    notice.value = "No selected leftover SKUs or target PO remain available.";
    return;
  }
  draft.value.stage = "review";
  if (changed && !notice.value) {
    notice.value =
      "Inventory changed while this move was open. Review the refreshed quantities.";
  }
  persistDraft();
}

function handleBack(): void {
  draft.value.stage = "edit";
  overrideConfirmed.value = false;
  persistDraft();
}

function handleApply(): void {
  if (refreshCurrentState()) {
    if (draft.value.mode === "fit") recalculateProportionalFit();
    notice.value ||=
      "Inventory changed. Review the updated move before applying.";
    persistDraft();
    return;
  }
  if (draft.value.mode === "fit" && selectedMoveOverCapacity.value) {
    if (recalculateProportionalFit()) {
      notice.value =
        "Quantities were recalculated to fit current capacity. Review the updated move before applying.";
      persistDraft();
    }
    return;
  }
  if (
    (draft.value.mode === "override" &&
      (selectedMoveOverCapacity.value || !selectedCapacityMeasurable.value) &&
      !overrideConfirmed.value) ||
    (draft.value.mode === "fit" &&
      (!selectedCapacityMeasurable.value || selectedMoveOverCapacity.value))
  ) {
    notice.value = "Resolve the capacity warning before applying this move.";
    return;
  }

  const dates = selectedTargetDates.value;
  const applyResult = props.onApply({
    selectedSkus: [...draft.value.selectedSkus],
    targetPoId: draft.value.targetPoId,
    quantitiesBySku: { ...draft.value.quantitiesBySku },
    mode: draft.value.mode,
    overrideConfirmed: overrideConfirmed.value,
    shipDateValue: cloneDate(dates?.shipDateValue),
    arrivalDate: cloneDate(dates?.arrivalDate),
  });
  if (applyResult.targetError) {
    notice.value = applyResult.targetError;
    return;
  }
  if (applyResult.requiresReview) {
    if (applyResult.quantitiesBySku) {
      draft.value.quantitiesBySku = {
        ...draft.value.quantitiesBySku,
        ...Object.fromEntries(
          Object.entries(applyResult.quantitiesBySku).map(([sku, quantity]) => [
            sku,
            String(quantity),
          ])
        ),
      };
    }
    notice.value =
      "Inventory or capacity changed. Review the refreshed quantities before applying.";
    persistDraft();
    return;
  }
  result.value = applyResult;
}

function handleDismiss(): void {
  props.onDismiss();
  modalStore.close();
}

function handleDone(): void {
  props.onDismiss();
  modalStore.close();
}

onMounted(() => {
  triggerBeforeOpen =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  void nextTick(() => firstFocusRef.value?.focus());
  const changed = refreshCurrentState();
  if (changed) draft.value.stage = "edit";
  if (draft.value.mode === "fit" && !draft.value.fitInitialized) {
    recalculateProportionalFit();
  } else if (changed) {
    persistDraft();
  }
});

onUnmounted(() => {
  if (triggerBeforeOpen?.isConnected) {
    void nextTick(() => triggerBeforeOpen?.focus());
  }
});
</script>

<template>
  <section class="leftover-move-modal">
    <header class="leftover-move-modal-header draggable">
      <div>
        <h2 :id="scopedId('leftover-move-heading')">
          {{ result ? "Move result" : "Move leftover SKUs" }}
        </h2>
        <p v-if="!result">
          Select one suggested PO and review the local quantity changes.
        </p>
      </div>
      <button
        type="button"
        class="leftover-move-modal-close"
        aria-label="Close move dialog"
        @mousedown.stop
        @click.stop="handleDismiss"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </header>

    <div v-if="result" class="leftover-move-modal-body">
      <p v-if="result.applied.length > 0" role="status">
        Applied {{ result.applied.length }} SKU
        {{ result.applied.length === 1 ? "move" : "moves" }} to local suggested
        PO quantities.
      </p>
      <p v-else role="status">No SKU quantities were moved.</p>
      <ul v-if="result.applied.length > 0" class="leftover-move-result-list">
        <li v-for="entry in result.applied" :key="entry.sku">
          {{ entry.sku }}: {{ entry.quantity }} units moved
        </li>
      </ul>
      <h3 v-if="result.skipped.length > 0">Not moved</h3>
      <ul v-if="result.skipped.length > 0" class="leftover-move-result-list">
        <li v-for="entry in result.skipped" :key="entry.sku" role="alert">
          {{ entry.sku }}: {{ entry.reason }}
        </li>
      </ul>
      <p v-if="result.targetError" class="leftover-move-warning" role="alert">
        {{ result.targetError }}
      </p>
    </div>

    <template v-else>
      <div v-if="draft.stage === 'edit'" class="leftover-move-modal-body">
        <fieldset class="leftover-move-mode">
          <legend>Capacity behavior</legend>
          <label :for="scopedId('leftover-move-mode-fit')">
            <input
              :id="scopedId('leftover-move-mode-fit')"
              type="radio"
              :name="scopedId('leftover-move-mode')"
              value="fit"
              :checked="draft.mode === 'fit'"
              @change="setMode('fit')"
            />
            Proportionally fit within capacity
          </label>
          <label :for="scopedId('leftover-move-mode-override')">
            <input
              :id="scopedId('leftover-move-mode-override')"
              type="radio"
              :name="scopedId('leftover-move-mode')"
              value="override"
              :checked="draft.mode === 'override'"
              @change="setMode('override')"
            />
            Move requested quantities with explicit override
          </label>
          <button
            v-if="draft.mode === 'fit'"
            type="button"
            class="btn btn-secondary leftover-move-fit-button"
            data-testid="recalculate-leftover-fit"
            @click="recalculateProportionalFit"
          >
            Recalculate proportional fit
          </button>
        </fieldset>

        <section class="leftover-move-section">
          <h3>Selected SKUs</h3>
          <p>
            Proportional fit pre-fills Move quantity; you can change it before
            review.
          </p>
          <div class="leftover-move-table-scroll">
            <table class="leftover-move-table">
              <thead>
                <tr>
                  <th scope="col">SKU</th>
                  <th scope="col">Description</th>
                  <th scope="col">Leftover</th>
                  <th scope="col">Move quantity</th>
                  <th scope="col">Availability</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in selectedRows"
                  :key="row.sku"
                  class="leftover-move-incoming"
                  data-testid="leftover-move-sku-row"
                >
                  <th scope="row">{{ row.sku }}</th>
                  <td>{{ row.description }}</td>
                  <td>{{ row.requestedUnits }}</td>
                  <td>
                    <div class="leftover-move-quantity-controls">
                      <button
                        type="button"
                        :aria-label="`Decrease move quantity for SKU ${row.sku}`"
                        :disabled="
                          !!getIssueForSku(row.sku) ||
                          (getDraftQuantity(row.sku) ?? 0) <= 0
                        "
                        @click="stepQuantity(row, -1)"
                      >
                        −
                      </button>
                      <input
                        :id="scopedId(`move-qty-${row.sku}`)"
                        :value="draft.quantitiesBySku[row.sku] ?? ''"
                        :aria-label="`Move quantity for SKU ${row.sku}`"
                        :aria-describedby="
                          getIssueForSku(row.sku)
                            ? scopedId(`move-qty-error-${row.sku}`)
                            : undefined
                        "
                        :aria-invalid="!!getIssueForSku(row.sku)"
                        type="number"
                        min="0"
                        :max="row.requestedUnits"
                        step="1"
                        @input="
                          updateQuantity(
                            row.sku,
                            ($event.target as HTMLInputElement).value
                          )
                        "
                      />
                      <button
                        type="button"
                        :aria-label="`Increase move quantity for SKU ${row.sku}`"
                        :disabled="
                          !!getIssueForSku(row.sku) ||
                          (getDraftQuantity(row.sku) ?? 0) >= row.requestedUnits
                        "
                        @click="stepQuantity(row, 1)"
                      >
                        +
                      </button>
                      <span>of {{ row.requestedUnits }}</span>
                    </div>
                    <p
                      v-if="getIssueForSku(row.sku)"
                      :id="scopedId(`move-qty-error-${row.sku}`)"
                      class="leftover-move-error"
                      role="alert"
                    >
                      {{ getIssueForSku(row.sku) }}
                    </p>
                  </td>
                  <td>
                    {{
                      formatCalendarDateForDisplay(row.shipDate) || "Not set"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="leftover-move-section">
          <h3>Suggested PO targets</h3>
          <div class="leftover-move-table-scroll">
            <table class="leftover-move-table leftover-move-target-table">
              <thead>
                <tr>
                  <th scope="col">Target</th>
                  <th scope="col">Available capacity</th>
                  <th scope="col">Projected ship</th>
                  <th scope="col">Projected arrival</th>
                  <th scope="col">Projected cube</th>
                  <th scope="col">Projected load</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="po in currentTargets" :key="po.id">
                  <th scope="row">
                    <label :for="scopedId(`leftover-move-target-${po.id}`)">
                      <input
                        :id="scopedId(`leftover-move-target-${po.id}`)"
                        type="radio"
                        :name="scopedId('leftover-move-target')"
                        :value="po.id"
                        :checked="po.id === draft.targetPoId"
                        @change="setTargetPo(po.id)"
                      />
                      {{ po.id === newPoId ? "New PO" : "PO" }}
                      {{ getPoDisplayNumber(po) }}
                    </label>
                  </th>
                  <td>
                    {{
                      formatMoveCubeFt3(getProjectedTarget(po).currentCapacity)
                    }}
                  </td>
                  <td>{{ formatDate(getProjectedTarget(po).shipDate) }}</td>
                  <td>{{ formatDate(getProjectedTarget(po).arrivalDate) }}</td>
                  <td>
                    {{
                      formatMoveCubeFt3(getProjectedTarget(po).projectedCube)
                    }}
                  </td>
                  <td>
                    {{
                      getProjectedTarget(po).projectedCapacityMeasurable
                        ? `${String(getProjectedTarget(po).projectedLoad)}%`
                        : "Unavailable"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="currentTarget && selectedTargetDates"
            class="leftover-move-date-fields"
          >
            <span :id="scopedId('target-ship-date-label')"
              >Target ship date</span
            >
            <DatePicker
              :id="scopedId('target-ship-date')"
              :aria-labelledby="scopedId('target-ship-date-label')"
              :model-value="selectedTargetDates.shipDateValue"
              placeholder="MM/DD/YYYY"
              @update:model-value="updateTargetDate('shipDateValue', $event)"
            />
            <span :id="scopedId('target-arrival-date-label')"
              >Target arrival date</span
            >
            <DatePicker
              :id="scopedId('target-arrival-date')"
              :aria-labelledby="scopedId('target-arrival-date-label')"
              :model-value="selectedTargetDates.arrivalDate"
              placeholder="MM/DD/YYYY"
              @update:model-value="updateTargetDate('arrivalDate', $event)"
            />
          </div>
        </section>

        <p
          v-for="warning in availabilityWarnings"
          :key="warning"
          class="leftover-move-warning"
          role="alert"
        >
          {{ warning }}
        </p>
        <p v-if="capacityError" class="leftover-move-warning" role="alert">
          {{ capacityError }}
        </p>
        <p v-if="notice" class="leftover-move-notice" role="status">
          {{ notice }}
        </p>
      </div>

      <div v-else class="leftover-move-modal-body">
        <h3>
          Review move to PO
          {{ currentTarget ? getPoDisplayNumber(currentTarget) : "" }}
        </h3>
        <p>
          SKUs that will be added to PO
          {{ currentTarget ? getPoDisplayNumber(currentTarget) : "" }} are
          highlighted. Applying updates local suggested PO quantities only;
          nothing is saved to the order system.
        </p>
        <p>
          Highlighted: will be added. Skipped: quantity 0. Not moved: resolve
          the quantity issue before moving.
        </p>
        <div class="leftover-move-table-scroll">
          <table class="leftover-move-table">
            <thead>
              <tr>
                <th scope="col">SKU</th>
                <th scope="col">Move quantity</th>
                <th scope="col">Review status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sku in draft.selectedSkus"
                :key="sku"
                :class="{
                  'leftover-move-incoming':
                    !getIssueForSku(sku) && (getDraftQuantity(sku) ?? 0) > 0,
                }"
              >
                <th scope="row">{{ sku }}</th>
                <td>{{ draft.quantitiesBySku[sku] ?? "0" }}</td>
                <td>
                  {{
                    getIssueForSku(sku)
                      ? `Not moved: ${getIssueForSku(sku)}`
                      : (getDraftQuantity(sku) ?? 0) > 0
                        ? `Will be added to PO ${currentTarget ? String(getPoDisplayNumber(currentTarget)) : ""}`
                        : "Skipped (quantity 0)"
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="currentTarget">
          Projected ship date:
          {{ formatDate(selectedTargetDates?.shipDateValue) }}; projected
          arrival date: {{ formatDate(selectedTargetDates?.arrivalDate) }};
          projected cube: {{ formatMoveCubeFt3(selectedProjectedCube) }};
          projected load:
          {{
            selectedCapacityMeasurable
              ? `${String(selectedProjectedTotals?.loadPercent ?? 0)}%`
              : "Unavailable"
          }}.
        </p>
        <p
          v-for="warning in availabilityWarnings"
          :key="warning"
          class="leftover-move-warning"
          role="alert"
        >
          {{ warning }}
        </p>
        <p v-if="notice" class="leftover-move-notice" role="status">
          {{ notice }}
        </p>
        <label
          v-if="
            draft.mode === 'override' &&
            (selectedMoveOverCapacity || !selectedCapacityMeasurable)
          "
          :for="scopedId('leftover-move-override-confirm')"
          class="leftover-move-override-confirm"
        >
          <input
            :id="scopedId('leftover-move-override-confirm')"
            v-model="overrideConfirmed"
            type="checkbox"
          />
          I understand this move may exceed capacity or cannot be measured.
        </label>
      </div>
    </template>

    <footer class="leftover-move-modal-footer">
      <template v-if="result">
        <button
          ref="firstFocusRef"
          type="button"
          class="btn btn-primary"
          data-testid="leftover-move-done"
          @click="handleDone"
        >
          Done
        </button>
      </template>
      <template v-else-if="draft.stage === 'edit'">
        <button
          ref="firstFocusRef"
          type="button"
          class="btn btn-secondary"
          @click="handleDismiss"
        >
          Close
        </button>
        <button
          type="button"
          class="btn btn-primary"
          data-testid="leftover-move-review"
          :disabled="selectedRows.length === 0 || !currentTarget"
          @click="handleReview"
        >
          Review move
        </button>
      </template>
      <template v-else>
        <button
          type="button"
          class="btn btn-secondary"
          data-testid="leftover-move-back"
          @click="handleBack"
        >
          Back
        </button>
        <button
          ref="firstFocusRef"
          type="button"
          class="btn btn-primary"
          data-testid="leftover-move-apply"
          :disabled="
            (draft.mode === 'override' &&
              (selectedMoveOverCapacity || !selectedCapacityMeasurable) &&
              !overrideConfirmed) ||
            (draft.mode === 'fit' && !selectedCapacityMeasurable)
          "
          @click="handleApply"
        >
          Apply move
        </button>
      </template>
    </footer>
  </section>
</template>

<style scoped>
.leftover-move-modal {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  color: var(--text-default, #172033);
}

.leftover-move-modal-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid var(--dashboard-divider, #e5e7eb);
  padding: 1rem;
}

.leftover-move-modal-header h2,
.leftover-move-modal-header p,
.leftover-move-section h3,
.leftover-move-modal-body h3 {
  margin: 0;
}

.leftover-move-modal-header h2 {
  font-size: 1.1rem;
}

.leftover-move-modal-header p {
  margin-top: 0.25rem;
  color: var(--text-supporting, #5d6878);
  font-size: 0.85rem;
}

.leftover-move-modal-close {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  background: transparent;
  color: var(--text-supporting, #5d6878);
  cursor: pointer;
}

.leftover-move-modal-body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.75rem;
  overflow: auto;
  padding: 1rem;
}

.leftover-move-mode {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  border: 1px solid var(--dashboard-border, #d1d5db);
  border-radius: 0.5rem;
  padding: 0.75rem;
}

.leftover-move-mode legend {
  padding-inline: 0.25rem;
  font-weight: 600;
}

.leftover-move-mode label,
.leftover-move-override-confirm {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.leftover-move-fit-button {
  margin-left: auto;
}

.leftover-move-section {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 0.5rem;
}

.leftover-move-section h3,
.leftover-move-modal-body h3 {
  font-size: 0.95rem;
}

.leftover-move-table-scroll {
  max-width: 100%;
  overflow: auto;
}

.leftover-move-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.leftover-move-table th,
.leftover-move-table td {
  border-bottom: 1px solid var(--dashboard-divider, #e5e7eb);
  padding: 0.45rem;
  text-align: left;
  vertical-align: top;
}

.leftover-move-table thead th {
  background: var(--dashboard-surface-muted, #f3f4f6);
  font-weight: 600;
}

.leftover-move-table input[type="number"] {
  width: 5.5rem;
  border: 1px solid #64748b;
  border-radius: 0.25rem;
  padding: 0.35rem;
  background: #fff;
  color: #111827;
}

.leftover-move-quantity-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.leftover-move-quantity-controls button {
  min-width: 2rem;
  min-height: 2rem;
  border: 1px solid #64748b;
  border-radius: 0.25rem;
  background: #fff;
  color: #111827;
  cursor: pointer;
}

.leftover-move-quantity-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.leftover-move-quantity-controls :focus-visible {
  outline: 2px solid #1d4ed8;
  outline-offset: 2px;
}

.leftover-move-incoming {
  background: var(--dashboard-info-bg, #eff6ff);
}

.leftover-move-date-fields {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) minmax(12rem, 2fr);
  align-items: center;
  gap: 0.5rem 0.75rem;
  max-width: 34rem;
}

.leftover-move-warning,
.leftover-move-error {
  color: var(--dashboard-danger, #b42318);
  font-size: 0.8rem;
}

.leftover-move-notice {
  color: var(--text-supporting, #5d6878);
  font-size: 0.85rem;
}

.leftover-move-error {
  margin: 0.25rem 0 0;
}

.leftover-move-result-list {
  margin: 0;
  padding-left: 1.25rem;
}

.leftover-move-modal-footer {
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  gap: 0.5rem;
  border-top: 1px solid var(--dashboard-divider, #e5e7eb);
  padding: 0.75rem 1rem;
}

@media (width <= 640px) {
  .leftover-move-date-fields {
    grid-template-columns: 1fr;
  }
}
</style>
